import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { forwardRef } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

import { buttonVariants } from './Button.tokens';
import { type ButtonProps } from './Button.types';

/**
 * The one button.
 *
 * `isLoading` disables as well as spins: a button that looks busy but still
 * fires is how a payment gets recorded twice. The spinner replaces the leading
 * icon rather than joining it, so the button does not change width mid-click and
 * move itself out from under the pointer.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    asChild = false,
    isLoading = false,
    startIcon,
    endIcon,
    children,
    disabled,
    type,
    ...props
  },
  ref,
) {
  const classes = cn(buttonVariants({ variant, size }), className);

  /**
   * Under `asChild`, Radix's Slot merges props onto the SINGLE child element —
   * so the button's own content (spinner, icons) cannot be rendered around it.
   * Passing three children here throws "Slot failed to slot onto its children",
   * which is what happens the first time someone wraps an anchor.
   *
   * The child owns its content in this mode. Loading and icon props do not
   * apply, and are deliberately not silently dropped elsewhere in the tree.
   */
  if (asChild) {
    return (
      <Slot ref={ref} className={classes} {...props}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      // An HTML button inside a form defaults to `submit`, which is how a
      // "Cancel" beside a form silently submits it.
      type={type ?? 'button'}
      className={classes}
      disabled={disabled === true || isLoading}
      aria-busy={isLoading ? true : undefined}
      {...props}
    >
      {isLoading ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : startIcon}
      {children}
      {isLoading ? null : endIcon}
    </button>
  );
});
